import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Icon,
  Input,
  PasswordInput,
  Text,
} from "@salestrak/ui";
import { useAuth } from "#app/lib/auth-context";
import { AuthRequestError } from "#app/lib/auth-service";
import { useAnalytics, AnalyticsContext, AuthEvent } from "#app/lib/analytics";
import { useRouter } from "#app/lib/hooks/router-compat";
import { useSplashAutoFocus } from "#app/lib/splash-context";
import { validateEmail } from "#app/lib/custom-field-utils";
import { GoogleSignInButton } from "./google-sign-in-button";

export function LoginForm() {
  const { signIn } = useAuth();
  const { track } = useAnalytics();
  const router = useRouter();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  // Field errors sit under their own field. A rejected submit (bad
  // credentials, network) is not one field's fault: it flags both fields
  // and reads in the form-level Alert above the CTA.
  const [emailError, setEmailError] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [formError, setFormError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [googlePending, setGooglePending] = React.useState(false);
  const emailRef = useSplashAutoFocus();

  const busy = submitting || googlePending;

  const handleLogin = async () => {
    if (busy) return;
    setFormError("");
    // Both fields are checked before returning, so one submit flags every
    // field that is wrong. The form is noValidate (the native bubble clashes
    // with the inline errors), so the format check lives here.
    const nextEmailError = !email.trim()
      ? "Email is required."
      : !validateEmail(email.trim())
        ? "Enter a valid email address."
        : "";
    const nextPasswordError = password.trim() ? "" : "Password is required.";
    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);
    if (nextEmailError || nextPasswordError) return;
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignIn, label: "success" });
    } catch (err: any) {
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignIn, label: "failure" });
      if (err instanceof AuthRequestError && err.code === "EMAIL_NOT_VERIFIED") {
        // The server re-sent the verification link on this attempt, so the
        // check-your-email screen (with its resend affordance) is accurate.
        router.replace({
          pathname: "/(auth)/verify-email-sent",
          params: { email: email.trim() },
        });
        return;
      }
      setFormError(err.message ?? "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // The form element owns submission, so Enter submits from either field.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleLogin();
  };

  // Rhythm matches the workspace screens: 12 label→input, 8 input→error,
  // 24 between fields, 48 fields→CTA (form gap 24 + 24 pad). A sign-in
  // failure belongs to the credential pair, so both fields flag invalid and
  // the message reads under the password field.
  //
  // The CTA stays enabled while fields are empty: the submit handler owns
  // the required-field checks and surfaces them inline, and a dimmed primary
  // button over the page fill is a mid-grey slab with no readable label.
  return (
    <Box as="form" noValidate direction="column" gap="lg" onSubmit={handleSubmit}>
      <GoogleSignInButton
        onError={setFormError}
        disabled={submitting}
        onPendingChange={setGooglePending}
      />

      <Box gap="base">
        <Text
          as="label"
          htmlFor="login-email"
          size="sm"
          weight="medium"
          variant="muted"
        >
          Email
        </Text>
        <Box gap="sm">
          <Input
            id="login-email"
            size="xl"
            type="email"
            placeholder="Enter your email address..."
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
              setFormError("");
            }}
            aria-invalid={!!emailError || !!formError}
            ref={emailRef}
            autoComplete="email"
            disabled={busy}
          />
          {emailError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {emailError}
            </Text>
          ) : null}
        </Box>
      </Box>

      <Box gap="base">
        <Box direction="row" align="center" justify="between">
          <Text
            as="label"
            htmlFor="login-password"
            size="sm"
            weight="medium"
            variant="muted"
          >
            Password
          </Text>
          {/* type="button": inside the form, a bare button submits. */}
          <Box
            as="button"
            type="button"
            cursor="pointer"
            onClick={() => router.push("/(auth)/forgot-password")}
          >
            <Text size="sm" variant="muted" underline="hover">
              Forgot password
            </Text>
          </Box>
        </Box>
        <Box gap="sm">
          <PasswordInput
            id="login-password"
            size="xl"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError("");
              setFormError("");
            }}
            aria-invalid={!!passwordError || !!formError}
            autoComplete="current-password"
            disabled={busy}
          />
          {passwordError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {passwordError}
            </Text>
          ) : null}
        </Box>
      </Box>

      {formError ? (
        <Alert variant="error">
          <Icon name="alert-circle" />
          <AlertTitle>{formError}</AlertTitle>
        </Alert>
      ) : null}

      <Box direction="column" paddingTop="lg">
        <Button
          type="submit"
          size="xl"
          loading={submitting}
          disabled={googlePending}
        >
          Log in
        </Button>
      </Box>
    </Box>
  );
}
